/**
 * B1081 — Évolution des coûts par service sur 12 mois
 * (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * L'alerte d'écart dit « ça a bougé ce mois-ci ». Elle ne montre pas la
 * dérive lente : une facture qui grimpe de 8 % par mois double en un an sans
 * jamais franchir le seuil de 50 %. Aires empilées (choix Eric) : on voit à
 * la fois le total du mois et la part de chaque service, avec un interrupteur
 * entre la vue « par usage » (ce qu'on peut corriger) et la vue « par
 * fournisseur » (ce qui apparaît sur la facture).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import axios from "axios";
import { ChartLine } from "@phosphor-icons/react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#002FA7", "#0EA5E9", "#14B8A6", "#F59E0B", "#8B5CF6"];

function SeriesTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const services = payload.filter((p) => p.dataKey !== "projection");
  const projection = payload.find((p) => p.dataKey === "projection");
  const total = services.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-lg"
      data-testid="consumption-series-tooltip"
    >
      <div className="font-semibold text-zinc-900">{label}</div>
      {services.map((p) => (
        <div key={p.dataKey} className="text-zinc-700">
          <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name} : {Number(p.value || 0).toFixed(2)} $
        </div>
      ))}
      {services.length > 0 && (
        <div className="mt-1 border-t border-zinc-100 pt-1 font-semibold text-zinc-900">
          Total : {total.toFixed(2)} $
        </div>
      )}
      {projection && projection.value != null && (
        <div className="text-zinc-600 mt-1">
          Projection : {Number(projection.value).toFixed(2)} $
        </div>
      )}
    </div>
  );
}

export const ConsumptionSeriesChart = () => {
  const { tr, lang } = useLang();
  const [data, setData] = useState(null);
  const [view, setView] = useState("usage");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const res = await axios.get(
        `${API}/api/org/ai-package/consumption-series?months=12${refresh ? "&refresh=true" : ""}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const labels = useMemo(
    // Tâche 1420 — La légende est nommée par le serveur, qui envoie les deux
    // langues ; un calcul d'avant cette tâche garde ses noms français.
    () => (view === "usage"
      ? (lang === "en" ? data?.usage_labels_en : null) || data?.usage_labels
      : (lang === "en" ? data?.provider_labels_en : null) || data?.provider_labels) || {},
    [data, view, lang]
  );
  const rows = useMemo(() => {
    const history = (data?.months || []).map((m) => ({
      label: m.label,
      total: m.total,
      ...(view === "usage" ? m.usage : m.provider),
    }));
    // B1083 — Prolongement pointillé : la projection doit se lire dans le
    // prolongement de la courbe, pas comme une seconde courbe indépendante.
    const projected = (data?.projection?.months || []).map((p) => ({
      label: p.label,
      projection: p.total,
      total: p.total,
    }));
    if (history.length && projected.length) {
      history[history.length - 1].projection = history[history.length - 1].total;
    }
    return [...history, ...projected];
  }, [data, view]);

  if (!data) return null;
  const keys = Object.keys(labels);
  const trend = data.trend || {};
  const projection = data.projection || {};
  const budget = data.budget_monthly_cad || null;
  // Un budget cent fois supérieur à la consommation écraserait l'échelle et
  // rendrait la courbe illisible : dans ce cas on l'annonce en texte.
  const maxSeries = Math.max(0, ...rows.map((r) => r.total || 0));
  const showBudgetLine = !!budget && budget <= maxSeries * 3;
  const rising = (trend.avg_change_pct ?? 0) >= 10;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5 mb-6"
      data-testid="consumption-series"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <ChartLine size={18} />
            {tr("Évolution des coûts sur 12 mois", "Cost trend over 12 months")}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
            {tr(
              "L'alerte d'écart dit « ça a bougé ce mois-ci » ; cette courbe montre la dérive lente, celle qu'aucun seuil ne révèle. Le mois en cours est incomplet, il est exclu du calcul de tendance.",
              "The gap alert says \"something moved this month\"; this curve shows the slow drift, the one no threshold reveals. The current month is incomplete and is left out of the trend.",
            )}
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-zinc-300 overflow-hidden text-xs">
          {[
            { value: "usage", label: tr("Par usage", "By usage") },
            { value: "provider", label: tr("Par fournisseur", "By provider") },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setView(opt.value)}
              className={`px-3 py-1.5 transition-colors ${
                view === opt.value
                  ? "bg-[#002FA7] text-white"
                  : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid={`consumption-series-view-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
          </div>
          {/* B1085/B1086 — Le calcul des 12 mois coûtait 2,6 s à chaque
              affichage : il est fait une fois par nuit, et à la demande ici. */}
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="px-3 py-1.5 rounded-lg border border-zinc-300 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="consumption-series-refresh"
          >
            {refreshing ? tr("Calcul…", "Calculating…") : tr("Recalculer", "Recalculate")}
          </button>
        </div>
      </div>

      {data.computed_at && (
        <p className="text-[11px] text-zinc-500 -mt-2 mb-3" data-testid="consumption-series-computed-at">
          {tr("Chiffres calculés le", "Figures computed on")}{" "}
          {new Date(data.computed_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", {
            dateStyle: "long",
            timeStyle: "short",
            timeZone: "America/Toronto",
          })}{" "}
          {tr("(heure de Montréal) · recalcul automatique chaque nuit.", "(Montréal time) · recomputed automatically every night.")}
        </p>
      )}

      <div className="h-64" data-testid="consumption-series-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" unit=" $" width={62} />
            <Tooltip content={<SeriesTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={labels[key]}
                stackId="cout"
                stroke={COLORS[i % COLORS.length]}
                fill={COLORS[i % COLORS.length]}
                fillOpacity={0.75}
              />
            ))}
            <Line
              type="monotone"
              dataKey="projection"
              name={tr("Projection", "Projection")}
              stroke="#111827"
              strokeDasharray="5 4"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            {budget && showBudgetLine ? (
              <ReferenceLine
                y={budget}
                stroke="#dc2626"
                strokeDasharray="3 3"
                label={{
                  value: `Budget ${budget.toFixed(0)} $`,
                  position: "insideTopRight",
                  fontSize: 10,
                  fill: "#dc2626",
                }}
              />
            ) : null}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {budget && !showBudgetLine ? (
        <p className="mt-2 text-[11px] text-zinc-500" data-testid="consumption-budget-offscale">
          {lang === "fr"
            ? `Budget mensuel de ${budget.toFixed(2)} $ : hors échelle du graphique, très au-dessus de la consommation actuelle. La comparaison chiffrée reste affichée ci-dessous.`
            : `Monthly budget of $${budget.toFixed(2)}: off the chart scale, far above current spending. The figures are still compared below.`}
        </p>
      ) : null}

      <p
        className={`mt-3 text-xs rounded-lg px-3 py-2 ${
          rising ? "bg-red-50 text-red-800" : "bg-zinc-50 text-zinc-600"
        }`}
        data-testid="consumption-series-trend"
      >
        {(lang === "en" ? trend.message_en : null) || trend.message}
      </p>

      {/* B1083 — Budget prévisionnel : trois montants chiffrés, comparés au
          budget mensuel configuré. */}
      {projection.months?.length ? (
        <div className="mt-3" data-testid="consumption-projection">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
            {tr("Budget prévisionnel — 3 prochains mois", "Forecast budget — next 3 months")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            {projection.months.map((p) => (
              <div
                key={p.month}
                className={`rounded-lg border px-3 py-2 ${
                  p.over_budget ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"
                }`}
                data-testid={`consumption-projection-${p.month}`}
              >
                <div className="text-[11px] text-zinc-500">{p.label}</div>
                <div
                  className={`text-sm font-semibold ${
                    p.over_budget ? "text-red-800" : "text-zinc-900"
                  }`}
                >
                  {p.total.toFixed(2)} $
                </div>
                {p.over_budget && (
                  <div className="text-[10px] text-red-700">{tr("au-delà du budget", "over budget")}</div>
                )}
              </div>
            ))}
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="text-[11px] text-zinc-500">{tr("Total du trimestre", "Quarter total")}</div>
              <div
                className="text-sm font-semibold text-zinc-900"
                data-testid="consumption-projection-quarter"
              >
                {Number(projection.quarter_total || 0).toFixed(2)} $
              </div>
            </div>
          </div>
          <p
            className={`mt-2 text-xs rounded-lg px-3 py-2 ${
              projection.first_overrun ? "bg-red-50 text-red-800" : "bg-zinc-50 text-zinc-600"
            }`}
            data-testid="consumption-projection-message"
          >
            {(lang === "en" ? projection.message_en : null) || projection.message}
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-zinc-500" data-testid="consumption-projection-empty">
          {(lang === "en" ? projection.message_en : null) || projection.message}
        </p>
      )}
    </section>
  );
};

export default ConsumptionSeriesChart;
